import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { IdCard, Users } from 'lucide-react'
import ActiveMark from '../../components/ActiveMark'
import DataTable from '../../components/DataTable'
import { useRemembered } from '../../components/listMemory'
import IconButton from '../../components/IconButton'
import Modal from '../../components/Modal'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import { useToast } from '../../components/toastContext'
import { listCompanies, listCompanyUsers } from './organisationsService'
import { ROLES } from '../../app/navigation'
import { useSession } from '../../lib/sessionContext'
import { companiesStrings as S } from './strings'
import './organisations.css'
import { serverDetail } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

/** قائمة المنظمات — Views/ManageCompanies/Index.cshtml. */
export default function CompaniesPage() {
  const toast = useToast()
  const { session } = useSession()
  /* `session` can be null while the bootstrap is in flight, so this reads false until it
     resolves rather than throwing — same guard as `SystemPage`. */
  const canReadProfile = session?.roles?.includes(ROLES.SettingsManagers) ?? false
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one — without this the table renders its "no
     records" state for a server error, which is the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [openCompany, setOpenCompany] = useState(null)
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [query, setQuery] = useRemembered('companies:query', '')

  const load = useCallback(
    () =>
      listCompanies()
        .then((response) => {
          setLoadError(null)
          setRows(response.rows)
          setLoading(false)
        })
        .catch((error) => {
          setLoadError(error)
          setRows([])
          setLoading(false)
          toast.error(serverDetail(error?.message) || S.loadFailed)
        }),
    [toast],
  )

  useEffect(() => {
    setLoading(true)
    load()
  }, [load])

  useEffect(() => {
    if (!openCompany) return
    setUsersLoading(true)
    listCompanyUsers(openCompany.id)
      .then((response) => {
        setUsers(response.rows)
        setUsersLoading(false)
      })
      .catch((error) => {
        setUsers([])
        setUsersLoading(false)
        toast.error(serverDetail(error?.message) || S.loadFailed)
      })
  }, [openCompany, toast])

  const columns = useMemo(
    () => [
      {
        key: 'arabicName',
        header: S.columns.arabicName,
        render: (row) => <bdi>{row.arabicName}</bdi>,
      },
      {
        key: 'englishName',
        header: S.columns.englishName,
        render: (row) => <bdi>{row.englishName}</bdi>,
      },
      {
        /* Two fields the API has always returned and the table never showed. On a tenant list
           they are what tells one organisation from another when the names are similar. */
        key: 'email',
        header: S.columns.email,
        render: (row) => <span dir="ltr">{row.email || '—'}</span>,
      },
      {
        key: 'registeredOn',
        header: S.columns.registeredOn,
        width: '140px',
        render: (row) => (
          <span className="org-date" dir="ltr">
            {displayDate(row.registeredOn) || '—'}
          </span>
        ),
      },
      {
        key: 'isActive',
        header: S.columns.isActive,
        width: '140px',
        align: 'center',
        value: (row) => (row.isActive ? S.active : S.inactive),
        render: (row) => (
          <ActiveMark active={Boolean(row.isActive)} label={row.isActive ? S.active : S.inactive} />
        ),
      },
      /* Two destinations, not three. The delete that used to sit here is gone: its handler
         hardcodes `canBeDeleted = false`, so the call can only ever refuse — and it decides that
         only after loading the company with eight `.Include()` collections, which on a real
         tenant does not return. See COSMETIC_UI.md. */
      {
        key: 'actions',
        header: '',
        align: 'end',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="data-table__row-actions">
            <IconButton
              icon={Users}
              label={S.viewUsers}
              tone="violet"
              onClick={() => setOpenCompany(row)}
            />
            {/* ONLY IF THE PROFILE IS READABLE. This list is guarded by `SystemManagers`
                and `/api/company-profile` by `SettingsManagers`, so for an account holding
                only the first the link led to a screen the server refuses. */}
            {canReadProfile && (
              <IconButton
                icon={IdCard}
                label={S.viewProfile}
                tone="info"
                as={Link}
                to={`/companies/${row.id}/profile`}
              />
            )}
          </span>
        ),
      },
    ],
    [canReadProfile],
  )

  const userColumns = useMemo(
    () => [
      {
        key: 'index',
        header: S.usersModal.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="org-index">{index + 1}</span>,
      },
      {
        key: 'username',
        header: S.usersModal.username,
        render: (row) => <span dir="ltr">{row.username}</span>,
      },
      {
        key: 'arabicName',
        header: S.usersModal.arabicName,
        render: (row) => <bdi>{row.arabicName}</bdi>,
      },
      {
        key: 'englishName',
        header: S.usersModal.englishName,
        render: (row) => <bdi>{row.englishName}</bdi>,
      },
      {
        key: 'email',
        header: S.usersModal.email,
        render: (row) => <span dir="ltr">{row.email}</span>,
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader title={S.title} count={loading ? undefined : rows.length} />

      <FilterBar search={{ value: query, onChange: setQuery, placeholder: S.columns.arabicName }} />

      <DataTable
        stateKey="companies"
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        /* The profile, where this account may read it — see the row's own link. */
        rowTo={canReadProfile ? (row) => `/companies/${row.id}/profile` : undefined}
      />

      <Modal
        open={!!openCompany}
        title={S.usersModal.title}
        size="lg"
        onClose={() => setOpenCompany(null)}
      >
        {/* The endpoint answers an empty array with HTTP 200 when it fails, so "nobody here"
            and "that did not work" look identical on the wire. The wording says the first
            without asserting it. */}
        <DataTable
          /* A list inside a sheet, and a different one per organisation: it neither takes
             the screen's `?page` nor remembers a position, or organisation B would open on
             organisation A's page 3. */
          stateKey={false}
          columns={userColumns}
          rows={users}
          loading={usersLoading}
          emptyMessage={S.usersModal.empty}
          getRowId={(row) => row.userId}
        />
      </Modal>

    </>
  )
}
